import type { MeasurementSessionRecord } from '~/models/measurementSession'
import type { CalendarDate } from '~/types/dates'
import { unwrapDate } from '~/types/dates'
import type { MeasurementSessionId } from '~/types/ids'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import type { SolaDatabase } from '../db'
import { type StorageError, toStorageError } from '../errors'
import { notifyWritten } from '../invalidation'
import { STORE } from '../schema'

export async function putMeasurementSession(
  db: SolaDatabase,
  record: MeasurementSessionRecord,
): Promise<Result<MeasurementSessionRecord, StorageError>> {
  try {
    await db.put(STORE.measurementSessions, record as never)
    notifyWritten(STORE.measurementSessions)
    return ok(record)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/** Every session, oldest first — the `date` index gives ascending order. */
export async function listMeasurementSessions(
  db: SolaDatabase,
): Promise<Result<readonly MeasurementSessionRecord[], StorageError>> {
  try {
    const rows = await db.getAllFromIndex(STORE.measurementSessions, 'date')
    return ok(rows as unknown as readonly MeasurementSessionRecord[])
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * The most recent session on or before a date.
 *
 * Used to show the current estimate and to compare against the previous week.
 */
export async function getLatestMeasurementSession(
  db: SolaDatabase,
  onOrBefore?: CalendarDate,
): Promise<Result<MeasurementSessionRecord | undefined, StorageError>> {
  const all = await listMeasurementSessions(db)
  if (!all.ok) return all

  const limit = onOrBefore === undefined ? undefined : unwrapDate(onOrBefore)
  const eligible =
    limit === undefined
      ? all.value
      : all.value.filter((session) => unwrapDate(session.date) <= limit)

  return ok(eligible[eligible.length - 1])
}

/**
 * Remove one session.
 *
 * Always an explicit user decision, never a cleanup the app performs on its own.
 * Sessions are keyed per save, so several can exist for one date; this is how
 * the user removes the one they did not mean to keep.
 *
 * A session owns no blobs, so nothing is orphaned by removing it. The derived
 * estimate is recomputed from whatever sessions remain on the next read rather
 * than being stored anywhere else, which is what makes deletion safe here.
 *
 * Deleting an ID that is not present is not an error — IndexedDB treats it as a
 * no-op, and so does this. Two taps on the same row should not produce a
 * failure message about data that is already gone.
 */
export async function deleteMeasurementSession(
  db: SolaDatabase,
  id: MeasurementSessionId,
): Promise<Result<void, StorageError>> {
  try {
    await db.delete(STORE.measurementSessions, id)
    notifyWritten(STORE.measurementSessions)
    return ok(undefined)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export async function getMeasurementSessionsForDate(
  db: SolaDatabase,
  date: CalendarDate,
): Promise<Result<readonly MeasurementSessionRecord[], StorageError>> {
  try {
    const rows = await db.getAllFromIndex(STORE.measurementSessions, 'date', unwrapDate(date))
    return ok(rows as unknown as readonly MeasurementSessionRecord[])
  } catch (cause) {
    return err(toStorageError(cause))
  }
}
